import AddIcon from '@mui/icons-material/Add';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { Alert, Button, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { useAppDispatch } from '@/app/store/hooks';
import { Loader } from '@/components/feedback/Loader';
import { DetailCard, DetailCardBody, DetailCardHeader } from '@/components/layout/DetailCard';
import { toastShown } from '@/features/ui';

import { AddCardDialog } from './AddCardDialog';
import { sortCardsByDefault, type Card } from './card';
import { ConfirmRemoveCardDialog } from './ConfirmRemoveCardDialog';
import { CreditCard } from './CreditCard';
import {
  useDeleteCardMutation,
  useGetSubscriptionQuery,
  useSetDefaultCardMutation,
} from './payments.api';
import { isStripeConfigured } from './stripe';

/**
 * The director's saved cards.
 *
 * Removing a card is destructive and irreversible, so it goes through a
 * confirmation naming the card — on a phone the delete control sits close
 * enough to everything else that an accidental tap is a real risk.
 *
 * Every write invalidates `Subscription`, so the list refetches afterwards.
 * One overlay covers the whole section for the write AND that refetch: adding,
 * removing or promoting a card reorders the list, so there is no single card
 * the progress belongs to, and a spinner that stopped between the two would
 * leave the old order on screen looking settled.
 */
export const PaymentDetailsCard = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading, isFetching, isError } = useGetSubscriptionQuery(undefined);
  const [deleteCard, { isLoading: isDeleting }] = useDeleteCardMutation();
  const [setDefaultCard, { isLoading: isSettingDefault }] = useSetDefaultCardMutation();
  const [pendingRemoval, setPendingRemoval] = useState<Card | null>(null);
  const [isAddingCard, setIsAddingCard] = useState(false);

  const defaultCardId = data?.customerCards?.defaultCard ?? '';

  // The card in use leads the list; Stripe returns them by creation date, which
  // is not an order that means anything to a director. The `?? []` fallback
  // lives inside the memo: outside it, a nullish response would build a new
  // array every render and the memo would never hold.
  const cards = useMemo(
    () => sortCardsByDefault(data?.customerCards?.allCards ?? [], defaultCardId),
    [data, defaultCardId],
  );

  // `isFetching` is what carries the overlay through the post-write refetch,
  // and through a card added in the dialog — that mutation lives there, but its
  // invalidation lands here.
  const isBusy = isDeleting || isSettingDefault || (isFetching && !isLoading);

  const handleMakeDefault = async (card: Card) => {
    try {
      await setDefaultCard({ cardId: card.id }).unwrap();
      dispatch(
        toastShown(`${card.brand} ending ${card.last4} is now your default card.`, 'success'),
      );
    } catch {
      dispatch(toastShown('That card could not be set as default.', 'error'));
    }
  };

  const handleConfirmRemoval = async () => {
    if (pendingRemoval === null) return;

    const { id, brand, last4 } = pendingRemoval;
    // The dialog closes on confirm rather than on the response. It is a modal:
    // while it is open the section behind it is `aria-hidden`, so the overlay
    // there would be both invisible and unannounced. Closing first hands the
    // progress to the section, and the toast reports the outcome.
    setPendingRemoval(null);

    try {
      await deleteCard({ cardId: id }).unwrap();
      dispatch(toastShown('Card removed.', 'success'));
    } catch {
      dispatch(toastShown(`${brand} ending ${last4} could not be removed.`, 'error'));
    }
  };

  return (
    <DetailCard>
      <DetailCardHeader
        icon={<CreditCardOutlinedIcon />}
        title="Saved payment details"
        subtitle="Cards used for annual reviews and invoices"
        action={
          <Button
            size="small"
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => {
              setIsAddingCard(true);
            }}
            disabled={!isStripeConfigured}
            className="w-full sm:w-auto"
          >
            Add card
          </Button>
        }
      />

      <DetailCardBody className="relative flex flex-col gap-3 p-4 sm:p-5">
        {isBusy && <CardListBusyOverlay />}

        <div inert={isBusy} className="flex flex-col gap-3">
          <CardList
            cards={cards}
            defaultCardId={defaultCardId}
            isLoading={isLoading}
            isError={isError}
            isBusy={isBusy}
            onDelete={setPendingRemoval}
            onMakeDefault={(card) => void handleMakeDefault(card)}
          />
        </div>

        {/* Stated in the card, not a tooltip: on touch a tooltip conveys
            nothing, and this is the answer to "why is the button greyed out". */}
        {!isStripeConfigured && (
          <Typography variant="caption" color="text.secondary">
            Adding a card is unavailable because payments are not configured for this
            environment.
          </Typography>
        )}
      </DetailCardBody>

      <AddCardDialog
        // Keyed so each open mounts fresh Stripe iframes: Elements exposes no
        // imperative reset, and a reopened dialog must not show the last
        // attempt's card number.
        key={isAddingCard ? 'open' : 'closed'}
        open={isAddingCard}
        onClose={() => {
          setIsAddingCard(false);
        }}
        isFirstCard={cards.length === 0}
      />

      <ConfirmRemoveCardDialog
        card={pendingRemoval}
        onCancel={() => {
          setPendingRemoval(null);
        }}
        onConfirm={() => void handleConfirmRemoval()}
      />
    </DetailCard>
  );
};

/**
 * One progress cover for the whole section.
 *
 * Over the section rather than over each card: every write here reorders or
 * replaces the list, so there is no single card the progress belongs to. The
 * caller pairs it with `inert` on the list, which takes that subtree out of
 * pointer and keyboard reach in one attribute — that is what stops a second
 * write landing on a list that is mid-change.
 */
const CardListBusyOverlay = () => (
  <Loader
    label="Updating your saved cards"
    className="absolute inset-0 z-10 h-full rounded-b-xl bg-surface/70 backdrop-blur-[1px]"
  />
);

interface CardListProps {
  cards: Card[];
  defaultCardId: string;
  isLoading: boolean;
  isError: boolean;
  isBusy: boolean;
  onDelete: (card: Card) => void;
  onMakeDefault: (card: Card) => void;
}

/** The four states a list of cards can be in. Split out so the card above holds
 *  only the mutations and the dialogs. */
const CardList = ({
  cards,
  defaultCardId,
  isLoading,
  isError,
  isBusy,
  onDelete,
  onMakeDefault,
}: CardListProps) => {
  if (isLoading) return <Loader label="Loading your saved cards" />;

  if (isError) {
    return (
      <Alert severity="error" role="alert" className="rounded-lg">
        Your saved cards could not be loaded. Please refresh to try again.
      </Alert>
    );
  }

  if (cards.length === 0) {
    return (
      <Alert severity="info" className="rounded-lg">
        You have no saved cards. Add one to pay for annual reviews and invoices faster.
      </Alert>
    );
  }

  return (
    <ul className="grid grid-cols-[repeat(auto-fill,minmax(240px,280px))] gap-4">
      {cards.map((card) => (
        <CreditCard
          key={card.id}
          card={card}
          isDefault={card.id === defaultCardId}
          onDelete={onDelete}
          onMakeDefault={onMakeDefault}
          isBusy={isBusy}
        />
      ))}
    </ul>
  );
};
